import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  FormGroup,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Repeat, Calendar, Trash2, Edit2, AlertCircle, Plus } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { RecurrenceConfig, RecurrenceFrequency, RecurrenceEndType, RecurrenceInstance } from '@/types';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';

interface TaskRecurrenceTabProps {
  taskId: string;
  canEdit?: boolean;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const TaskRecurrenceTab: React.FC<TaskRecurrenceTabProps> = ({ taskId, canEdit = true }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Form state
  const [frequency, setFrequency] = useState<RecurrenceFrequency>(RecurrenceFrequency.WEEKLY);
  const [intervalValue, setIntervalValue] = useState(1);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([]);
  const [dayOfMonth, setDayOfMonth] = useState<number>(1);
  const [monthOfYear, setMonthOfYear] = useState<number>(1);
  const [endType, setEndType] = useState<RecurrenceEndType>(RecurrenceEndType.NEVER);
  const [endValue, setEndValue] = useState<string>('');
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  // Fetch recurrence config
  const { data: recurrenceConfig, isLoading, error } = useQuery({
    queryKey: QUERY_KEYS.taskRecurrence(taskId),
    queryFn: () => taskService.getRecurrence(taskId),
    enabled: !!taskId,
    retry: false, // Don't retry if task has no recurrence (404)
  });

  // Fetch instances
  const { data: instances } = useQuery<RecurrenceInstance[]>({
    queryKey: QUERY_KEYS.taskRecurrenceInstances(taskId),
    queryFn: () => taskService.getRecurrenceInstances(taskId, { limit: 10 }),
    enabled: !!taskId && !!recurrenceConfig,
  });

  // Create recurrence mutation
  const createMutation = useMutation({
    mutationFn: () => taskService.createRecurrence(taskId, {
      frequency,
      intervalValue,
      daysOfWeek: frequency === RecurrenceFrequency.WEEKLY ? daysOfWeek : undefined,
      dayOfMonth: frequency === RecurrenceFrequency.MONTHLY || frequency === RecurrenceFrequency.YEARLY ? dayOfMonth : undefined,
      monthOfYear: frequency === RecurrenceFrequency.YEARLY ? monthOfYear : undefined,
      endType,
      endValue: endType === RecurrenceEndType.AFTER ? endValue : 
                endType === RecurrenceEndType.ON ? endDate?.format('YYYY-MM-DD') : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskRecurrence(taskId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId) });
      setIsCreating(false);
      showSnackbar('Recurrence pattern created', 'success');
    },
    onError: (error: any) => {
      showSnackbar(error?.response?.data?.message ?? 'Failed to create recurrence', 'error');
    },
  });

  // Update recurrence mutation
  const updateMutation = useMutation({
    mutationFn: () => {
      if (!recurrenceConfig) throw new Error('No config to update');
      return taskService.updateRecurrence(taskId, recurrenceConfig.id, {
        frequency,
        intervalValue,
        daysOfWeek: frequency === RecurrenceFrequency.WEEKLY ? daysOfWeek : undefined,
        dayOfMonth: frequency === RecurrenceFrequency.MONTHLY || frequency === RecurrenceFrequency.YEARLY ? dayOfMonth : undefined,
        monthOfYear: frequency === RecurrenceFrequency.YEARLY ? monthOfYear : undefined,
        endType,
        endValue: endType === RecurrenceEndType.AFTER ? endValue :
                  endType === RecurrenceEndType.ON ? endDate?.format('YYYY-MM-DD') : undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskRecurrence(taskId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskRecurrenceInstances(taskId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId) });
      setIsEditing(false);
      showSnackbar('Recurrence pattern updated', 'success');
    },
    onError: (error: any) => {
      showSnackbar(error?.response?.data?.message ?? 'Failed to update recurrence', 'error');
    },
  });

  // Delete recurrence mutation
  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!recurrenceConfig) throw new Error('No config to delete');
      return taskService.deleteRecurrence(taskId, recurrenceConfig.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskRecurrence(taskId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId) });
      setShowDeleteConfirm(false);
      showSnackbar('Recurrence series deleted', 'success');
    },
    onError: (error: any) => {
      showSnackbar(error?.response?.data?.message ?? 'Failed to delete recurrence', 'error');
    },
  });

  // Load existing config into form
  const loadConfigIntoForm = (config: RecurrenceConfig) => {
    setFrequency(config.frequency);
    setIntervalValue(config.intervalValue);
    setDaysOfWeek(config.daysOfWeek ?? []);
    setDayOfMonth(config.dayOfMonth ?? 1);
    setMonthOfYear(config.monthOfYear ?? 1);
    setEndType(config.endType);
    if (config.endType === RecurrenceEndType.AFTER) {
      setEndValue(config.endValue ?? '');
    } else if (config.endType === RecurrenceEndType.ON && config.endValue) {
      setEndDate(dayjs(config.endValue));
    }
  };

  const handleStartEdit = () => {
    if (recurrenceConfig) {
      loadConfigIntoForm(recurrenceConfig);
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsCreating(false);
  };

  const handleCreate = () => {
    // Validation
    if (frequency === RecurrenceFrequency.WEEKLY && daysOfWeek.length === 0) {
      showSnackbar('Please select at least one day of the week', 'warning');
      return;
    }
    if (endType === RecurrenceEndType.AFTER && (!endValue || parseInt(endValue) <= 0)) {
      showSnackbar('Please enter a valid number of occurrences', 'warning');
      return;
    }
    if (endType === RecurrenceEndType.ON && !endDate) {
      showSnackbar('Please select an end date', 'warning');
      return;
    }
    createMutation.mutate();
  };

  const handleUpdate = () => {
    // Same validation as create
    if (frequency === RecurrenceFrequency.WEEKLY && daysOfWeek.length === 0) {
      showSnackbar('Please select at least one day of the week', 'warning');
      return;
    }
    if (endType === RecurrenceEndType.AFTER && (!endValue || parseInt(endValue) <= 0)) {
      showSnackbar('Please enter a valid number of occurrences', 'warning');
      return;
    }
    if (endType === RecurrenceEndType.ON && !endDate) {
      showSnackbar('Please select an end date', 'warning');
      return;
    }
    updateMutation.mutate();
  };

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  const handleDayToggle = (day: number) => {
    setDaysOfWeek(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort()
    );
  };

  const generatePreviewText = (config?: RecurrenceConfig): string => {
    const cfg = config ?? {
      frequency,
      intervalValue,
      daysOfWeek,
      dayOfMonth,
      monthOfYear,
      endType,
      endValue,
    };

    let text = 'Repeats ';
    
    // Frequency
    if (cfg.frequency === RecurrenceFrequency.DAILY) {
      text += cfg.intervalValue === 1 ? 'daily' : `every ${cfg.intervalValue} days`;
    } else if (cfg.frequency === RecurrenceFrequency.WEEKLY) {
      text += cfg.intervalValue === 1 ? 'weekly' : `every ${cfg.intervalValue} weeks`;
      if (cfg.daysOfWeek && cfg.daysOfWeek.length > 0) {
        const dayNames = cfg.daysOfWeek.map(d => DAYS_OF_WEEK.find(dow => dow.value === d)?.label).filter(Boolean);
        text += ` on ${dayNames.join(', ')}`;
      }
    } else if (cfg.frequency === RecurrenceFrequency.MONTHLY) {
      text += cfg.intervalValue === 1 ? 'monthly' : `every ${cfg.intervalValue} months`;
      text += ` on day ${cfg.dayOfMonth}`;
    } else if (cfg.frequency === RecurrenceFrequency.YEARLY) {
      text += cfg.intervalValue === 1 ? 'yearly' : `every ${cfg.intervalValue} years`;
      text += ` on ${MONTHS[(cfg.monthOfYear ?? 1) - 1]} ${cfg.dayOfMonth}`;
    }

    // End type
    if (cfg.endType === RecurrenceEndType.NEVER) {
      text += '';//, never ends';
    } else if (cfg.endType === RecurrenceEndType.AFTER) {
      text += `, ends after ${cfg.endValue} occurrences`;
    } else if (cfg.endType === RecurrenceEndType.ON) {
      text += `, ends on ${cfg.endValue}`;
    }

    return text;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load recurrence configuration
      </Alert>
    );
  }

  // Render existing config (not editing)
  if (recurrenceConfig && !isEditing) {
    return (
      <Box sx={{ py: 2 }}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Repeat size={20} style={{ color: '#6366f1' }} />
              <Typography variant="h6" fontWeight={600}>
                Recurring Task
              </Typography>
            </Box>
            {canEdit && (
              <Box display="flex" gap={1}>
                <IconButton size="small" onClick={handleStartEdit} sx={{ color: '#4F46E5' }}>
                  <Edit2 size={16} />
                </IconButton>
                <IconButton size="small" onClick={() => setShowDeleteConfirm(true)} sx={{ color: '#ef4444' }}>
                  <Trash2 size={16} />
                </IconButton>
              </Box>
            )}
          </Box>

          <Alert icon={<Repeat size={16} />} severity="info" sx={{ mb: 2 }}>
            {generatePreviewText(recurrenceConfig)}
          </Alert>

          {/* Configuration Details */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Configuration Details:
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 1, mt: 1 }}>
              <Typography variant="body2" fontWeight={500}>Frequency:</Typography>
              <Typography variant="body2">{recurrenceConfig.frequency.charAt(0).toUpperCase() + recurrenceConfig.frequency.slice(1)}</Typography>
              
              <Typography variant="body2" fontWeight={500}>Interval:</Typography>
              <Typography variant="body2">Every {recurrenceConfig.intervalValue} {recurrenceConfig.frequency}(s)</Typography>
              
              {recurrenceConfig.daysOfWeek && recurrenceConfig.daysOfWeek.length > 0 && (
                <>
                  <Typography variant="body2" fontWeight={500}>Days:</Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {recurrenceConfig.daysOfWeek.map(d => (
                      <Chip key={d} label={DAYS_OF_WEEK[d].label} size="small" />
                    ))}
                  </Box>
                </>
              )}
              
              <Typography variant="body2" fontWeight={500}>Ends:</Typography>
              <Typography variant="body2">
                {recurrenceConfig.endType === RecurrenceEndType.NEVER && 'Never'}
                {recurrenceConfig.endType === RecurrenceEndType.AFTER && `After ${recurrenceConfig.endValue} occurrences`}
                {recurrenceConfig.endType === RecurrenceEndType.ON && `On ${recurrenceConfig.endValue}`}
              </Typography>
            </Box>
          </Box>

          {/* Upcoming Instances */}
          {instances && instances.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Next {instances.length} Occurrences:
              </Typography>
              <List dense>
                {instances.map((instance, index) => (
                  <ListItem key={instance.id} sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Calendar size={14} style={{ color: '#6b7280' }} />
                          <Typography variant="body2">
                            {dayjs(instance.instanceDate).format('MMM DD, YYYY (ddd)')}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Paper>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
          <DialogTitle>Delete Recurring Series?</DialogTitle>
          <DialogContent>
            <Alert severity="warning" icon={<AlertCircle size={20} />} sx={{ mb: 2 }}>
              This will delete the entire recurring series and all future task instances that haven't been completed.
            </Alert>
            <Typography variant="body2">
              Are you sure you want to delete this recurrence pattern?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button
              onClick={handleDelete}
              color="error"
              variant="contained"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Series'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Render form (creating or editing)
  const isFormActive = isCreating || isEditing;

  return (
    <Box sx={{ py: 2 }}>
      {!isFormActive && !recurrenceConfig && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Repeat size={48} style={{ opacity: 0.3, margin: '0 auto 16px' }} />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            This task is not recurring
          </Typography>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => setIsCreating(true)}
              sx={{ mt: 2 }}
            >
              Make Task Recurring
            </Button>
          )}
        </Box>
      )}

      {isFormActive && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {isEditing ? 'Edit Recurrence Pattern' : 'Create Recurrence Pattern'}
          </Typography>

          {/* Frequency Selector */}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <FormLabel>Repeat Frequency</FormLabel>
            <ToggleButtonGroup
              value={frequency}
              exclusive
              onChange={(_, value) => value && setFrequency(value)}
              fullWidth
              sx={{ mt: 1 }}
            >
              <ToggleButton value={RecurrenceFrequency.DAILY}>Daily</ToggleButton>
              <ToggleButton value={RecurrenceFrequency.WEEKLY}>Weekly</ToggleButton>
              <ToggleButton value={RecurrenceFrequency.MONTHLY}>Monthly</ToggleButton>
              <ToggleButton value={RecurrenceFrequency.YEARLY}>Yearly</ToggleButton>
            </ToggleButtonGroup>
          </FormControl>

          {/* Interval */}
          <TextField
            fullWidth
            label={`Repeat every (${frequency}s)`}
            type="number"
            value={intervalValue}
            onChange={(e) => setIntervalValue(Math.max(1, parseInt(e.target.value) || 1))}
            InputProps={{ inputProps: { min: 1, max: 365 } }}
            sx={{ mt: 2 }}
          />

          {/* Weekly: Days of Week */}
          {frequency === RecurrenceFrequency.WEEKLY && (
            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormLabel>Repeat on</FormLabel>
              <FormGroup row sx={{ mt: 1 }}>
                {DAYS_OF_WEEK.map(day => (
                  <FormControlLabel
                    key={day.value}
                    control={
                      <Checkbox
                        checked={daysOfWeek.includes(day.value)}
                        onChange={() => handleDayToggle(day.value)}
                      />
                    }
                    label={day.label}
                  />
                ))}
              </FormGroup>
            </FormControl>
          )}

          {/* Monthly: Day of Month */}
          {frequency === RecurrenceFrequency.MONTHLY && (
            <TextField
              fullWidth
              label="Day of Month"
              type="number"
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(Math.max(1, Math.min(31, parseInt(e.target.value) || 1)))}
              InputProps={{ inputProps: { min: 1, max: 31 } }}
              sx={{ mt: 2 }}
            />
          )}

          {/* Yearly: Month and Day */}
          {frequency === RecurrenceFrequency.YEARLY && (
            <Box display="flex" gap={2} mt={2}>
              <AppSelect
                label="Month"
                value={monthOfYear}
                onChange={(e) => setMonthOfYear(e.target.value as number)}
              >
                {MONTHS.map((month, index) => (
                  <AppMenuItem key={index} value={index + 1}>{month}</AppMenuItem>
                ))}
              </AppSelect>
              <TextField
                fullWidth
                label="Day"
                type="number"
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(Math.max(1, Math.min(31, parseInt(e.target.value) || 1)))}
                InputProps={{ inputProps: { min: 1, max: 31 } }}
              />
            </Box>
          )}

          {/* End Type */}
          <FormControl component="fieldset" sx={{ mt: 3 }}>
            <FormLabel>Ends</FormLabel>
            <RadioGroup value={endType} onChange={(e) => setEndType(e.target.value as RecurrenceEndType)}>
              <FormControlLabel value={RecurrenceEndType.NEVER} control={<Radio />} label="Never" />
              <FormControlLabel
                value={RecurrenceEndType.AFTER}
                control={<Radio />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>After</span>
                    {endType === RecurrenceEndType.AFTER && (
                      <TextField
                        size="small"
                        type="number"
                        value={endValue}
                        onChange={(e) => setEndValue(e.target.value)}
                        InputProps={{ inputProps: { min: 1 } }}
                        sx={{ width: 80 }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    <span>occurrences</span>
                  </Box>
                }
              />
              <FormControlLabel
                value={RecurrenceEndType.ON}
                control={<Radio />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>On</span>
                    {endType === RecurrenceEndType.ON && (
                      <DatePicker
                        value={endDate}
                        onChange={(date) => setEndDate(date)}
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { width: 160 },
                            onClick: (e: React.MouseEvent) => e.stopPropagation(),
                          },
                        }}
                      />
                    )}
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          {/* Preview */}
          <Alert icon={<Repeat size={16} />} severity="info" sx={{ mt: 3 }}>
            <strong>Preview:</strong> {generatePreviewText()}
          </Alert>

          {/* Actions */}
          <Box display="flex" gap={2} mt={3}>
            <Button
              variant="contained"
              onClick={isEditing ? handleUpdate : handleCreate}
              disabled={createMutation.isPending || updateMutation.isPending}
              fullWidth
            >
              {isEditing ? 'Update Pattern' : 'Create Pattern'}
            </Button>
            <Button
              onClick={handleCancelEdit}
              disabled={createMutation.isPending || updateMutation.isPending}
              fullWidth
            >
              Cancel
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default TaskRecurrenceTab;
